import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Offcanvas from 'react-bootstrap/Offcanvas'
import Spinner from 'react-bootstrap/Spinner'

import {
  exportDraftEventsAssistantReport,
  generateDraftEventsAssistantSpeech,
  getDraftEventsAssistantProgress,
  postDraftEventsAssistantMessage,
} from '../../utilities/api'
import AuthService from '../../utilities/services/auth.service'

import StellaChart from './StellaChart'
import {
  ChevronIcon,
  CloseIcon,
  ComposeIcon,
  ContextIcon,
  InfoIcon,
  MicIcon,
  PaperclipIcon,
  SendIcon,
  SparkleIcon,
  SpeakerIcon,
  SpeakerOffIcon,
  StopIcon,
  TraceIcon,
} from './StellaIcons'
import StellaMarkdown from './StellaMarkdown'
import StellaReport from './StellaReport'
import useSpeechDictation, { joinWords } from './useSpeechDictation'
import useSpeechPlayback from './useSpeechPlayback'

import './draftEventsAssistantHost.scss'

const EMPTY_STATE = {
  mode: null,
  homeTeam: null,
  sport: null,
  league: null,
  venueName: null,
  roomName: null,
  roomUUID: null,
  categoryName: null,
  categoryId: null,
  subCategoryNames: [],
  subCategoryIds: [],
  summary: null,
  displayStartTime: true,
  eventImageCandidates: [],
  selectedEventImageUrl: null,
  eventImageSkipped: false,
  seasonName: null,
  existingSeasonId: null,
  createSeason: false,
  seasonStarts: null,
  seasonEnds: null,
  seasonPackageName: null,
  packageId: null,
  packageName: null,
  createSeasonPackage: false,
  seatmapId: null,
  standardAdmissionPrice: null,
  doorsOpenOffsetMinutes: 120,
  generalOnsaleOffsetDays: 30,
  eventVisibility: null,
  from: null,
  to: null,
  eventCount: null,
  eventNamePrefix: null,
  scheduleConfirmed: false,
  setupConfirmed: false,
  imagesConfirmed: false,
  events: [],
  imageCandidates: [],
  createdSeasonId: null,
  createdPackageId: null,
  createdPackageUuid: null,
  createdEventCount: null,
}

const initialMessages = []

// Shown only on an empty conversation, so the panel opens with something to
// click rather than a blank page and a long placeholder.
const SUGGESTED_PROMPTS = [
  'What is my next event?',
  'How were sales this past week?',
  'How do I place seats on hold?',
  'Create one draft event at The Fillmore on Oct 3',
]

// Older sessions persisted a scripted greeting as the first assistant message.
// Drop it so cached conversations no longer replay the event-only pitch.
const LEGACY_GREETING_PREFIX = 'I can help three ways:'

const stripLegacyGreeting = (messages) =>
  asArray(messages).filter(
    (message) =>
      !(
        message?.role === 'assistant' &&
        String(message?.content || '').startsWith(LEGACY_GREETING_PREFIX)
      )
  )

// Whether replies are read aloud is a preference about the room the admin is
// in, so it outlives the conversation the way the panel's other state does not.
const SPEECH_PREFERENCE_KEY = 'draft-events-assistant:speak-replies'

const loadSpeechPreference = () => {
  try {
    return localStorage.getItem(SPEECH_PREFERENCE_KEY) === 'true'
  } catch (_error) {
    return false
  }
}

let messageSequence = 0

// Playback is addressed by message, so each turn needs an id that survives the
// re-renders an index-based key would not.
const nextMessageId = () => {
  messageSequence += 1
  return `m${Date.now().toString(36)}-${messageSequence}`
}

const messageKey = (message, index) =>
  message?.id || `${message?.role || 'message'}-${index}`

const enabled = () =>
  String(process.env.REACT_APP_ENABLE_AI_DRAFT_EVENTS_ASSISTANT || '')
    .trim()
    .toLowerCase() === 'true'

const asArray = (value) => (Array.isArray(value) ? value : [])

const sanitizeLocalGuideUrl = (value) => {
  const url = String(value || '').trim()
  return /^\/stella-help-center\/guides\/[a-z0-9-]+\/[a-z0-9._-]+\.pdf$/i.test(
    url
  )
    ? url
    : null
}

// The backend adds resource groups over time, so read each one defensively.
const normalizeResourceSuggestions = (input) => ({
  venues: asArray(input?.venues),
  rooms: asArray(input?.rooms),
  seatmaps: asArray(input?.seatmaps),
  categories: asArray(input?.categories),
  subCategories: asArray(input?.subCategories),
  seasons: asArray(input?.seasons),
  packages: asArray(input?.packages),
})

// Reuse the normalizer so a reset can never drop a group the panel renders.
const emptyResourceSuggestions = () => normalizeResourceSuggestions()

const loadConversation = (key) => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(key))
    // A stored conversation can predate newer state fields, so fill the gaps.
    if (saved?.messages && saved?.state) {
      return {
        ...saved,
        messages: stripLegacyGreeting(saved.messages),
        state: { ...EMPTY_STATE, ...saved.state },
      }
    }
  } catch (_error) {
    // Start a fresh conversation when persisted state is invalid.
  }
  return { messages: initialMessages, state: EMPTY_STATE, missing: [] }
}

const formatDate = (value) => {
  if (!value) return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

const optionLabel = (option, index) =>
  option?.name ||
  option?.label ||
  option?.title ||
  (option?.id ? `ID ${option.id}` : `Option ${index + 1}`)

const optionKey = (option, index) =>
  String(option?.uuid || option?.id || option?.name || index)

// Candidates may arrive flat or grouped under an options array.
const flattenImageOptions = (candidates) =>
  asArray(candidates)
    .flatMap((candidate) =>
      Array.isArray(candidate?.options) ? candidate.options : [candidate]
    )
    .filter((option) => option?.imageUrl)

const quoted = (value) => `“${String(value).trim()}”`

// Spells out the work the confirmed plan implies so a long create request
// reads as steps in progress rather than a stalled spinner.
const buildCreationSteps = (state) => {
  const steps = []

  if (state.existingSeasonId) {
    steps.push({
      key: 'season',
      label: state.seasonName
        ? `Add to existing season ${quoted(state.seasonName)}`
        : `Add to existing season ID ${state.existingSeasonId}`,
    })
  } else if (state.seasonName || state.createSeason) {
    steps.push({
      key: 'season',
      label: state.seasonName
        ? `Create season ${quoted(state.seasonName)}`
        : 'Create the new season',
    })
  }

  const eventCount =
    state.mode === 'placeholders'
      ? Number(state.eventCount) || 0
      : asArray(state.events).length

  if (eventCount > 0) {
    steps.push({
      key: 'events',
      label: `Create ${eventCount} unpublished draft ${
        eventCount === 1 ? 'event' : 'events'
      }${state.venueName ? ` at ${state.venueName}` : ''}`,
    })
  }

  if (state.packageId) {
    steps.push({
      key: 'template',
      label: `Clone ticketing setup from ${
        state.packageName
          ? quoted(state.packageName)
          : `package ID ${state.packageId}`
      }`,
    })
  }

  if (state.createSeasonPackage) {
    steps.push({
      key: 'season-package',
      label: state.seasonPackageName
        ? `Create unpublished draft package ${quoted(state.seasonPackageName)}`
        : 'Create the unpublished draft package',
    })
  }

  if (
    state.selectedEventImageUrl ||
    asArray(state.imageCandidates).some((candidate) =>
      asArray(candidate?.options).length ? !candidate.skipped : false
    )
  ) {
    steps.push({ key: 'artwork', label: 'Upload the approved artwork' })
  }

  return steps
}

const formatElapsed = (seconds) => {
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  return `${minutes}m ${String(seconds % 60).padStart(2, '0')}s`
}

// How often the panel asks the server what it is working on. A create spends
// ~25s per event, so this is frequent enough to look live and cheap enough to
// run for the several minutes a large batch takes.
const PROGRESS_POLL_MS = 2000
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024
const ACCEPTED_ATTACHMENT_TYPES =
  '.csv,.json,.png,.xls,.xlsx,text/csv,application/json,image/png,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

const errorMessage = (error, fallback) => {
  if (error?.response?.status === 429) {
    return 'The assistant is handling too many requests right now. Wait a moment and send your message again.'
  }
  if (error?.code === 'ECONNABORTED') {
    return 'The assistant took too long to respond. Try sending a shorter request.'
  }
  return (
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  )
}

export default function DraftEventsAssistantHost({
  featureEnabled = enabled(),
} = {}) {
  const organization = AuthService.getOrg()?.[0]
  const storageKey = `draft-events-assistant:${organization?.uuid || 'none'}`
  const initial = useMemo(() => loadConversation(storageKey), [storageKey])

  const [open, setOpen] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [attachment, setAttachment] = useState(null)
  const [messages, setMessages] = useState(initial.messages)
  const [state, setState] = useState(initial.state)
  const [missing, setMissing] = useState(initial.missing || [])
  const [citations, setCitations] = useState(initial.citations || [])
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState(initial.result || null)
  const [venueSuggestions, setVenueSuggestions] = useState(
    initial.venueSuggestions || []
  )
  const [resourceSuggestions, setResourceSuggestions] = useState(
    normalizeResourceSuggestions(initial.resourceSuggestions)
  )
  // Researched artwork is often hotlink-protected or no longer hosted, and an
  // option the browser cannot load is one the server cannot download either.
  const [unloadableImages, setUnloadableImages] = useState([])
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  // What the server reports it is doing right now, polled while a request runs.
  const [progress, setProgress] = useState(null)
  // The step trace is open by default so a long create still explains itself,
  // but it collapses to a single line for quick questions.
  const [traceOpen, setTraceOpen] = useState(true)
  // The entry point depends on an org setting that can be toggled while the app
  // is open, so re-read it when organization settings are saved.
  const [, setOrgSettingsRefreshKey] = useState(0)
  const [speakReplies, setSpeakReplies] = useState(loadSpeechPreference)
  const bodyRef = useRef(null)
  const fileInputRef = useRef(null)

  // Dictated phrases land in the composer rather than sending straight away, so
  // the admin can fix a misheard venue or date before Stella acts on it.
  const appendDictation = useCallback(
    (text) => setPrompt((current) => joinWords(current, text)),
    []
  )
  const dictation = useSpeechDictation({ onTranscript: appendDictation })
  const generateSpeech = useCallback(
    (text) =>
      generateDraftEventsAssistantSpeech({
        organizationUUID: organization?.uuid,
        text,
      }),
    [organization?.uuid]
  )
  const playback = useSpeechPlayback({ generateSpeech })

  // Reading a reply aloud into an open microphone would dictate Stella back to
  // herself, so speaking always takes the microphone down first.
  const speakMessage = useCallback(
    (text, id) => {
      dictation.stop()
      playback.speak(text, id)
    },
    [dictation.stop, playback.speak]
  )

  const isSuperAdmin = AuthService.isSuperAdminOrganizer()
  const aiAssistantEnabled = Boolean(organization?.setting?.aiAssistant)

  const isPlaceholders = state.mode === 'placeholders'
  const isSingle = state.mode === 'single'
  const isSeasonPackage = state.mode === 'season_package'

  useEffect(() => {
    const handler = () => setOrgSettingsRefreshKey((key) => key + 1)
    window.addEventListener('blocktickets:org-settings-updated', handler)
    return () =>
      window.removeEventListener('blocktickets:org-settings-updated', handler)
  }, [])

  useEffect(() => {
    const next = loadConversation(storageKey)
    setMessages(next.messages)
    setState(next.state)
    setMissing(next.missing || [])
    setCitations(next.citations || [])
    setResult(next.result || null)
    setVenueSuggestions(next.venueSuggestions || [])
    setResourceSuggestions(
      normalizeResourceSuggestions(next.resourceSuggestions)
    )
    setUnloadableImages([])
  }, [storageKey])

  useEffect(() => {
    sessionStorage.setItem(
      storageKey,
      JSON.stringify({
        messages,
        state,
        missing,
        citations,
        result,
        venueSuggestions,
        resourceSuggestions,
      })
    )
  }, [
    storageKey,
    messages,
    state,
    missing,
    citations,
    result,
    venueSuggestions,
    resourceSuggestions,
  ])

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight
    }
  }, [messages, sending, result])

  // Keeping the microphone open past a closed panel or an in-flight request
  // would keep recording work the admin can no longer see or edit.
  useEffect(() => {
    if (!open || sending) dictation.stop()
  }, [open, sending, dictation.stop])

  // A voice still reading an answer after the panel is gone has no visible way
  // to be stopped.
  useEffect(() => {
    if (!open) playback.stop()
  }, [open, playback.stop])

  useEffect(() => {
    try {
      localStorage.setItem(SPEECH_PREFERENCE_KEY, String(speakReplies))
    } catch (_error) {
      // A blocked storage quota should not disable the toggle itself.
    }
  }, [speakReplies])

  // A creating request can run for a while, so surface how long it has been
  // working to make it obvious the panel is still live.
  useEffect(() => {
    if (!sending) {
      setElapsedSeconds(0)
      return undefined
    }
    const startedAt = Date.now()
    const timer = setInterval(
      () => setElapsedSeconds(Math.round((Date.now() - startedAt) / 1000)),
      1000
    )
    return () => clearInterval(timer)
  }, [sending])

  // The reply arrives in one response after work that can run for minutes, so
  // poll the server for the step it is on rather than guessing from local state.
  useEffect(() => {
    if (!sending || !organization?.uuid) {
      setProgress(null)
      return undefined
    }

    let active = true
    const poll = async () => {
      try {
        const response = await getDraftEventsAssistantProgress(
          organization.uuid
        )
        if (active) setProgress(response?.data?.progress || null)
      } catch (_error) {
        // Progress is cosmetic; the request itself still reports the outcome.
      }
    }

    poll()
    const timer = setInterval(poll, PROGRESS_POLL_MS)
    return () => {
      active = false
      clearInterval(timer)
    }
  }, [sending, organization?.uuid])

  const sendMessage = async (text, action = null) => {
    const trimmed = String(text ?? prompt).trim()
    const fileToSend = action ? null : attachment
    if ((!trimmed && !fileToSend) || sending || !organization?.uuid) return
    const messageText =
      trimmed ||
      'Analyze the attached file and summarize the important findings.'

    const priorMessages = messages
    setMessages((current) => [
      ...current,
      {
        id: nextMessageId(),
        role: 'user',
        content: messageText,
        attachmentName: fileToSend?.name || null,
      },
    ])
    setPrompt('')
    if (fileToSend) setAttachment(null)
    setSending(true)
    setError('')
    // The previous answer is stale the moment a new question is asked.
    playback.stop()

    try {
      const requestBody = {
        organizationUUID: organization.uuid,
        message: messageText,
        messages: priorMessages,
        state,
        action,
      }
      const response = fileToSend
        ? await postDraftEventsAssistantMessage(requestBody, fileToSend)
        : await postDraftEventsAssistantMessage(requestBody)
      const data = response?.data || {}
      setState({ ...EMPTY_STATE, ...(data.state || {}) })
      setMissing(data.missing || [])
      setCitations(data.citations || [])
      setVenueSuggestions(data.venueSuggestions || [])
      setResourceSuggestions(
        normalizeResourceSuggestions(data.resourceSuggestions)
      )
      if (data.created) setResult(data.created)
      const replyId = nextMessageId()
      const replyText =
        data.reply ||
        data.error?.message ||
        data.message ||
        'I could not prepare a response.'
      setMessages((current) => [
        ...current,
        {
          id: replyId,
          role: 'assistant',
          content: replyText,
          charts: asArray(data.charts),
          screenshots: asArray(data.screenshots),
          guideUrl: sanitizeLocalGuideUrl(data.guideUrl),
          report: data.report || null,
        },
      ])
      if (speakReplies) speakMessage(replyText, replyId)
      if (data.createError) setError(data.createError)
      if (fileToSend && fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    } catch (requestError) {
      if (fileToSend) setAttachment(fileToSend)
      const text = errorMessage(
        requestError,
        'Stella could not respond. Send your message again to retry.'
      )
      setMessages((current) => [
        ...current,
        {
          id: nextMessageId(),
          role: 'assistant',
          content: text,
        },
      ])
      setError(text)
    } finally {
      setSending(false)
    }
  }

  // Stella renders a bounded table in chat; exporting sends that same table to
  // the server, which builds the file and returns a download URL. For reports
  // that carry a source range (order listings), the server re-runs the report
  // and exports the full dataset instead of just the preview rows.
  const exportReport = async (format, report) => {
    if (!organization?.uuid) {
      throw new Error('No active organization')
    }
    const src = report?.source || null
    const canFull =
      report?.reportId === 'order-listings' && src?.from && src?.to
    const source = canFull
      ? {
          reportId: report.reportId,
          from: src.from,
          to: src.to,
          eventUuid: src.eventUuid || null,
          full: true,
        }
      : null
    const response = await exportDraftEventsAssistantReport({
      organizationUUID: organization.uuid,
      format,
      title: report?.title || 'report',
      columns: asArray(report?.table?.columns),
      rows: asArray(report?.table?.rows),
      source,
    })
    return response?.data?.export || null
  }

  const toggleSpeakReplies = () => {
    const next = !speakReplies
    setSpeakReplies(next)
    if (!next) playback.stop()
  }

  const resetConversation = () => {
    playback.stop()
    setMessages(initialMessages)
    setState(EMPTY_STATE)
    setMissing([])
    setCitations([])
    setResult(null)
    setVenueSuggestions([])
    setResourceSuggestions(emptyResourceSuggestions())
    setUnloadableImages([])
    setAttachment(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
    setError('')
    sessionStorage.removeItem(storageKey)
  }

  const planEvents = isPlaceholders ? [] : asArray(state.events)
  const planConfirmed = isSingle
    ? state.setupConfirmed
    : state.scheduleConfirmed
  const isLoadable = (option) => !unloadableImages.includes(option?.imageUrl)
  const markUnloadable = (imageUrl) =>
    setUnloadableImages((current) =>
      current.includes(imageUrl) ? current : [...current, imageUrl]
    )
  const eventImageOptions = flattenImageOptions(
    state.eventImageCandidates
  ).filter(isLoadable)
  const attractionCandidates = asArray(state.imageCandidates)
    .map((candidate) => ({
      ...candidate,
      options: asArray(candidate.options).filter(isLoadable),
    }))
    .filter((candidate) => candidate.options.length > 0)
  const needsArtworkApproval =
    !state.imagesConfirmed &&
    (eventImageOptions.length > 0 || attractionCandidates.length > 0)
  const artworkUnavailable =
    !state.imagesConfirmed &&
    unloadableImages.length > 0 &&
    eventImageOptions.length === 0 &&
    attractionCandidates.length === 0

  const venueOptions = asArray(resourceSuggestions.venues).length
    ? resourceSuggestions.venues
    : asArray(venueSuggestions)

  const suggestionGroups = [
    {
      key: 'venues',
      title: 'Did you mean this venue?',
      options: venueOptions,
      isSelected: (option) => option?.name && option.name === state.venueName,
      buildMessage: (option, index) =>
        `Use venue ${optionLabel(option, index)}.`,
      buildAction: (option, index) => ({
        type: 'select_venue',
        venueName: optionLabel(option, index),
      }),
    },
    {
      key: 'rooms',
      title: 'Which room should I use?',
      options: resourceSuggestions.rooms,
      isSelected: (option) =>
        (option?.uuid && option.uuid === state.roomUUID) ||
        (option?.name && option.name === state.roomName),
      // A suggested room can belong to another venue, so name that venue.
      buildLabel: (option, index) =>
        option?.venueName && option.venueName !== state.venueName
          ? `${optionLabel(option, index)} · ${option.venueName}`
          : optionLabel(option, index),
      buildMessage: (option, index) =>
        `Use room ${optionLabel(option, index)}.`,
      buildAction: (option, index) => ({
        type: 'select_room',
        roomName: optionLabel(option, index),
        roomUUID: option?.uuid || null,
      }),
    },
    {
      key: 'seatmaps',
      title: 'Which seatmap / configuration should I use?',
      options: resourceSuggestions.seatmaps,
      isSelected: (option) => option?.id && option.id === state.seatmapId,
      buildMessage: (option, index) =>
        `Use seatmap ${optionLabel(option, index)}.`,
      buildAction: (option, index) => ({
        type: 'select_seatmap',
        seatmapId: option?.id || null,
        name: optionLabel(option, index),
      }),
    },
    {
      key: 'categories',
      title: 'Which category fits this event?',
      options: resourceSuggestions.categories,
      isSelected: (option) =>
        (option?.id && option.id === state.categoryId) ||
        (option?.name && option.name === state.categoryName),
      buildMessage: (option, index) =>
        `Use category ${optionLabel(option, index)}.`,
      buildAction: (option, index) => ({
        type: 'select_category',
        categoryName: optionLabel(option, index),
        categoryId: option?.id || null,
      }),
    },
    {
      key: 'seasons',
      title: 'Add these events to an existing season?',
      options: resourceSuggestions.seasons,
      isSelected: (option) =>
        (option?.id && option.id === state.existingSeasonId) ||
        (option?.name && option.name === state.seasonName),
      buildMessage: (option, index) =>
        `Use season ${optionLabel(option, index)}.`,
      buildAction: (option, index) => ({
        type: 'select_season',
        seasonName: optionLabel(option, index),
        seasonId: option?.id || null,
      }),
    },
    {
      key: 'subCategories',
      title: 'Which sub-categories should I use?',
      options: resourceSuggestions.subCategories,
      isSelected: (option) =>
        (option?.id && asArray(state.subCategoryIds).includes(option.id)) ||
        (option?.name && asArray(state.subCategoryNames).includes(option.name)),
      buildMessage: (option, index) =>
        `Use sub-category ${optionLabel(option, index)}.`,
      buildAction: (option, index) => ({
        type: 'select_sub_category',
        subCategoryName: optionLabel(option, index),
        subCategoryId: option?.id || null,
        selected: true,
      }),
    },
    {
      key: 'packages',
      title: 'Clone ticketing setup from a template package?',
      options: resourceSuggestions.packages,
      isSelected: (option) =>
        (option?.id && option.id === state.packageId) ||
        (option?.name && option.name === state.packageName),
      buildMessage: (option, index) =>
        `Use template package ${optionLabel(option, index)}.`,
      buildAction: (option, index) => ({
        type: 'select_package',
        packageName: optionLabel(option, index),
        packageId: option?.id || null,
      }),
    },
  ]
    .map((group) => ({ ...group, options: asArray(group.options) }))
    .filter((group) => group.options.length > 0)

  const isCreating = Boolean(state.mode) && missing.length === 0
  const creationSteps = isCreating ? buildCreationSteps(state) : []

  const progressCurrent = Number(progress?.current) || 0
  const progressTotal = Number(progress?.total) || 0
  const showProgressBar = progressCurrent > 0 && progressTotal > 0
  const progressPercent = showProgressBar
    ? Math.min(100, Math.round((progressCurrent / progressTotal) * 100))
    : 0

  const loadingCopy = () => {
    // The server knows the actual step; local state can only guess at it.
    if (progress?.label) return progress.label
    if (isCreating) {
      return isSeasonPackage
        ? 'Creating your season/package…'
        : 'Creating your unpublished draft events…'
    }
    if (isPlaceholders) return 'Preparing the placeholder plan…'
    if (isSingle) return 'Preparing the event setup…'
    if (isSeasonPackage) return 'Preparing the season/package setup…'
    if (state.mode === 'schedule') {
      return 'Researching the schedule and preparing a response…'
    }
    return 'Thinking through your request…'
  }

  const resultRows = result
    ? [
        result.mode === 'season_package'
          ? null
          : ['Draft events', `${result.eventCount || 0} created, unpublished`],
        ['Season', result.seasonId ? `ID ${result.seasonId}` : ''],
        [
          'Draft package',
          result.packageUuid
            ? result.packageUuid
            : result.seasonPackageId
              ? `ID ${result.seasonPackageId}`
              : '',
        ],
      ]
        .filter(Boolean)
        .filter((row) => row[1])
    : []

  if (
    !featureEnabled ||
    !AuthService.isLoggedIn() ||
    !organization?.uuid ||
    !(isSuperAdmin || aiAssistantEnabled)
  ) {
    return null
  }

  return (
    <div className="draft-events-assistant">
      <Button
        className="draft-events-assistant__trigger"
        type="button"
        variant="light"
        onClick={() => setOpen(true)}
        aria-label="Open Ask Stella"
        aria-expanded={open}
      >
        <span
          className="draft-events-assistant__trigger-icon"
          aria-hidden="true"
        >
          <SparkleIcon width={14} height={14} />
        </span>
        <span className="draft-events-assistant__trigger-label">
          Ask Stella
        </span>
      </Button>

      {/* Stella runs alongside the admin rather than on top of it: no backdrop
          to dim or swallow clicks, and `scroll` both leaves the body scrollable
          and drops react-bootstrap's focus trap so page fields stay usable. */}
      <Offcanvas
        show={open}
        onHide={() => setOpen(false)}
        placement="end"
        backdrop={false}
        scroll
        aria-modal={false}
        className="draft-events-assistant__drawer"
      >
        <Offcanvas.Header className="draft-events-assistant__header">
          <div className="draft-events-assistant__brand">
            <span
              className="draft-events-assistant__brand-mark"
              aria-hidden="true"
            >
              <SparkleIcon width={15} height={15} />
            </span>
            <Offcanvas.Title className="draft-events-assistant__brand-name">
              Stella
            </Offcanvas.Title>
            <span className="draft-events-assistant__brand-beta">Beta</span>
          </div>
          <div className="draft-events-assistant__header-actions">
            {playback.supported ? (
              <button
                type="button"
                className={`draft-events-assistant__icon-button${
                  speakReplies ? ' is-active' : ''
                }`}
                aria-pressed={speakReplies}
                aria-label={
                  speakReplies
                    ? 'Turn off spoken replies'
                    : 'Read replies aloud'
                }
                title={
                  speakReplies ? 'Spoken replies on' : 'Read replies aloud'
                }
                onClick={toggleSpeakReplies}
              >
                {speakReplies ? <SpeakerIcon /> : <SpeakerOffIcon />}
              </button>
            ) : null}
            <button
              type="button"
              className="draft-events-assistant__icon-button"
              aria-label="New conversation"
              title="New conversation"
              disabled={sending}
              onClick={resetConversation}
            >
              <ComposeIcon />
            </button>
            <button
              type="button"
              className="draft-events-assistant__icon-button"
              aria-label="Close Ask Stella"
              title="Close"
              onClick={() => setOpen(false)}
            >
              <CloseIcon />
            </button>
          </div>
        </Offcanvas.Header>

        <Offcanvas.Body className="draft-events-assistant__layout">
          <div className="draft-events-assistant__messages" ref={bodyRef}>
            {messages.length === 0 && !sending ? (
              <section className="draft-events-assistant__welcome">
                <span
                  className="draft-events-assistant__welcome-mark"
                  aria-hidden="true"
                >
                  <SparkleIcon width={22} height={22} />
                </span>
                <h2 className="draft-events-assistant__welcome-title">
                  Hi, I am Stella
                </h2>
                <p className="draft-events-assistant__welcome-copy">
                  Ask about your events, sales, and tickets, how to use
                  BlockTickets, or tell me to build draft events for{' '}
                  {organization.name || 'this organization'}.
                </p>
                <div className="draft-events-assistant__suggestions">
                  {SUGGESTED_PROMPTS.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      className="draft-events-assistant__suggestion"
                      disabled={sending}
                      onClick={() => sendMessage(suggestion)}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </section>
            ) : null}

            {messages.map((item, index) => (
              <div
                key={messageKey(item, index)}
                className={`draft-events-assistant__message draft-events-assistant__message--${item.role}`}
              >
                {/* Stella answers in markdown; the admin's own turn is the
                    literal text they typed, so it is not parsed as any. */}
                {item.role === 'assistant' ? (
                  <StellaMarkdown text={item.content} />
                ) : (
                  <>
                    {item.attachmentName ? (
                      <div className="draft-events-assistant__message-attachment">
                        <PaperclipIcon width={13} height={13} />
                        {item.attachmentName}
                      </div>
                    ) : null}
                    {String(item.content || '')
                      .split('\n')
                      .map((line, lineIndex) => (
                        <p key={lineIndex} className="mb-1">
                          {line}
                        </p>
                      ))}
                  </>
                )}
                {item.guideUrl ? (
                  <a
                    className="draft-events-assistant__guide"
                    href={item.guideUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span className="draft-events-assistant__guide-kicker">
                      How-to guide
                    </span>
                    <span className="draft-events-assistant__guide-title">
                      Download the PDF
                    </span>
                  </a>
                ) : null}
                {asArray(item.screenshots).length > 0 ? (
                  <div className="draft-events-assistant__screenshots">
                    {asArray(item.screenshots).map(
                      (screenshot, screenshotIndex) => (
                        <figure
                          key={`${screenshot.url}-${screenshotIndex}`}
                          className="draft-events-assistant__screenshot"
                        >
                          <a
                            href={screenshot.url}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={`Open screenshot ${screenshotIndex + 1} full size`}
                          >
                            <img
                              src={screenshot.url}
                              alt={
                                screenshot.alt || 'BlockTickets help screenshot'
                              }
                              loading="lazy"
                            />
                          </a>
                          {screenshot.caption ? (
                            <figcaption>{screenshot.caption}</figcaption>
                          ) : null}
                        </figure>
                      )
                    )}
                  </div>
                ) : null}
                {asArray(item.charts).map((chart, chartIndex) => (
                  <StellaChart key={chartIndex} chart={chart} />
                ))}
                {item.report ? (
                  <StellaReport report={item.report} onExport={exportReport} />
                ) : null}
                {item.role === 'assistant' && playback.supported ? (
                  <div className="draft-events-assistant__message-actions">
                    <button
                      type="button"
                      className="draft-events-assistant__listen"
                      aria-label={
                        playback.activeId === messageKey(item, index)
                          ? 'Stop reading this reply'
                          : 'Read this reply aloud'
                      }
                      onClick={() =>
                        playback.activeId === messageKey(item, index)
                          ? playback.stop()
                          : speakMessage(item.content, messageKey(item, index))
                      }
                    >
                      {playback.activeId === messageKey(item, index) ? (
                        <>
                          <StopIcon width={12} height={12} />
                          Stop
                        </>
                      ) : (
                        <>
                          <SpeakerIcon width={13} height={13} />
                          Listen
                        </>
                      )}
                    </button>
                  </div>
                ) : null}
              </div>
            ))}

            {!result && isPlaceholders && state.eventCount ? (
              <section className="draft-events-assistant__card">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>Placeholder event plan</strong>
                  <Badge bg="info">Creates automatically</Badge>
                </div>
                <div className="small">
                  <div>
                    <span className="text-muted">Venue:</span>{' '}
                    {state.venueName || '—'}
                  </div>
                  <div>
                    <span className="text-muted">Events:</span>{' '}
                    {state.eventCount} evenly spaced placeholders
                  </div>
                  <div>
                    <span className="text-muted">From:</span>{' '}
                    {formatDate(state.from) || '—'}
                  </div>
                  <div>
                    <span className="text-muted">To:</span>{' '}
                    {formatDate(state.to) || '—'}
                  </div>
                  {state.eventNamePrefix ? (
                    <div>
                      <span className="text-muted">Name prefix:</span>{' '}
                      {state.eventNamePrefix}
                    </div>
                  ) : null}
                  {state.homeTeam ? (
                    <div>
                      <span className="text-muted">Home team:</span>{' '}
                      {state.homeTeam}
                    </div>
                  ) : null}
                </div>
              </section>
            ) : null}

            {!result && isSeasonPackage ? (
              <section className="draft-events-assistant__card">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>
                    {state.createSeason && state.createSeasonPackage
                      ? 'Season + draft package plan'
                      : state.createSeasonPackage
                        ? 'Draft package plan'
                        : 'Season plan'}
                  </strong>
                  <Badge bg={state.setupConfirmed ? 'success' : 'warning'}>
                    {state.setupConfirmed ? 'Confirmed' : 'Needs approval'}
                  </Badge>
                </div>
                <div className="small">
                  <div>
                    <span className="text-muted">Venue:</span>{' '}
                    {state.venueName || '—'}
                    {state.roomName ? ` · ${state.roomName}` : ''}
                  </div>
                  {state.createSeason || !state.existingSeasonId ? (
                    <>
                      <div>
                        <span className="text-muted">Season:</span>{' '}
                        {state.seasonName || '—'}
                      </div>
                      <div>
                        <span className="text-muted">Dates:</span>{' '}
                        {formatDate(state.seasonStarts) || '—'}
                        {state.seasonEnds
                          ? ` – ${formatDate(state.seasonEnds)}`
                          : ''}
                      </div>
                    </>
                  ) : (
                    <div>
                      <span className="text-muted">Existing season id:</span>{' '}
                      {state.existingSeasonId}
                    </div>
                  )}
                  {state.createSeasonPackage ? (
                    <>
                      <div>
                        <span className="text-muted">Package:</span>{' '}
                        {state.seasonPackageName || 'Untitled draft package'}
                      </div>
                      <div>
                        <span className="text-muted">Seatmap id:</span>{' '}
                        {state.seatmapId || '—'}
                      </div>
                      <div className="text-muted">
                        Status will be unpublished
                      </div>
                    </>
                  ) : null}
                </div>
                {!state.setupConfirmed ? (
                  <Button
                    size="sm"
                    variant="outline-primary"
                    className="mt-2"
                    disabled={sending}
                    onClick={() =>
                      sendMessage(
                        'I confirm this season/package setup exactly as shown.',
                        { type: 'confirm_setup' }
                      )
                    }
                  >
                    Confirm setup
                  </Button>
                ) : null}
              </section>
            ) : null}

            {!result && planEvents.length > 0 ? (
              <section className="draft-events-assistant__card">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>
                    {isSingle ? 'Draft event plan' : 'Proposed home schedule'}
                  </strong>
                  <Badge bg={planConfirmed ? 'success' : 'warning'}>
                    {planConfirmed ? 'Confirmed' : 'Needs approval'}
                  </Badge>
                </div>
                <div className="small mb-2">
                  <span className="text-muted">Venue:</span>{' '}
                  {state.venueName || 'Choose a venue'}
                  {state.roomName ? ` · ${state.roomName}` : ''}
                </div>
                <div className="draft-events-assistant__schedule">
                  {planEvents.map((event, index) => (
                    <div key={`${event.start}-${index}`} className="py-2">
                      <div className="fw-semibold">{event.name}</div>
                      <div className="small text-muted">
                        {formatDate(event.start) ||
                          'Start time to be confirmed'}
                        {event.end ? ` – ${formatDate(event.end)}` : ''}
                      </div>
                      {event.sourceUrl ? (
                        <a
                          href={event.sourceUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="small"
                        >
                          Schedule source
                        </a>
                      ) : null}
                    </div>
                  ))}
                </div>
                {!planConfirmed ? (
                  <Button
                    size="sm"
                    variant="outline-primary"
                    className="mt-2"
                    disabled={sending}
                    onClick={() =>
                      sendMessage(
                        isSingle
                          ? 'I confirm this event setup exactly as shown.'
                          : 'I confirm this home schedule exactly as shown.',
                        {
                          type: isSingle ? 'confirm_setup' : 'confirm_schedule',
                        }
                      )
                    }
                  >
                    {isSingle ? 'Confirm event setup' : 'Confirm schedule'}
                  </Button>
                ) : null}
              </section>
            ) : null}

            {!result && eventImageOptions.length > 0 ? (
              <section className="draft-events-assistant__card">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>Event image</strong>
                  <Badge
                    bg={
                      state.selectedEventImageUrl
                        ? 'success'
                        : state.eventImageSkipped
                          ? 'secondary'
                          : 'info'
                    }
                  >
                    {state.selectedEventImageUrl
                      ? 'Selected'
                      : state.eventImageSkipped
                        ? 'Skipped'
                        : 'Using option 1'}
                  </Badge>
                </div>
                <div className="draft-events-assistant__artwork-options">
                  {eventImageOptions.map((option, index) => (
                    <div
                      className="draft-events-assistant__artwork-option"
                      key={`${option.imageUrl}-${index}`}
                    >
                      <img
                        src={option.imageUrl}
                        alt={`Event artwork option ${index + 1}`}
                        referrerPolicy="no-referrer"
                        onError={() => markUnloadable(option.imageUrl)}
                      />
                      <Button
                        size="sm"
                        variant={
                          state.selectedEventImageUrl === option.imageUrl
                            ? 'success'
                            : 'outline-secondary'
                        }
                        disabled={sending}
                        onClick={() =>
                          sendMessage(
                            `Use event image option ${index + 1} (${option.imageUrl}).`,
                            {
                              type: 'select_event_image',
                              imageUrl: option.imageUrl,
                            }
                          )
                        }
                      >
                        {state.selectedEventImageUrl === option.imageUrl
                          ? 'Selected'
                          : `Use ${index + 1}`}
                      </Button>
                      {option.sourceUrl ? (
                        <a
                          href={option.sourceUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="small"
                        >
                          Source
                        </a>
                      ) : null}
                    </div>
                  ))}
                </div>
                <Button
                  size="sm"
                  variant="link"
                  className="px-0"
                  disabled={sending}
                  onClick={() =>
                    sendMessage('Skip the event image.', {
                      type: 'select_event_image',
                      imageUrl: null,
                    })
                  }
                >
                  Skip event image
                </Button>
              </section>
            ) : null}

            {!result && attractionCandidates.length > 0 ? (
              <section className="draft-events-assistant__card">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>Attraction artwork</strong>
                  <Badge bg={state.imagesConfirmed ? 'success' : 'warning'}>
                    {state.imagesConfirmed ? 'Confirmed' : 'Needs approval'}
                  </Badge>
                </div>
                {attractionCandidates.map((candidate) => (
                  <div
                    key={candidate.attractionName}
                    className="draft-events-assistant__artwork-group"
                  >
                    <div className="fw-semibold mb-2">
                      {candidate.attractionName}
                      {!candidate.selectedImageUrl && !candidate.skipped ? (
                        <span className="ms-2 small text-muted fw-normal">
                          Using option 1
                        </span>
                      ) : null}
                      {candidate.skipped ? (
                        <span className="ms-2 small text-muted fw-normal">
                          Skipped
                        </span>
                      ) : null}
                    </div>
                    <div className="draft-events-assistant__artwork-options">
                      {candidate.options?.map((option, index) => (
                        <div
                          className="draft-events-assistant__artwork-option"
                          key={`${option.imageUrl}-${index}`}
                        >
                          <img
                            src={option.imageUrl}
                            alt={`${candidate.attractionName} option ${index + 1}`}
                            referrerPolicy="no-referrer"
                            onError={() => markUnloadable(option.imageUrl)}
                          />
                          <Button
                            size="sm"
                            variant={
                              candidate.selectedImageUrl === option.imageUrl
                                ? 'success'
                                : 'outline-secondary'
                            }
                            disabled={sending}
                            onClick={() =>
                              sendMessage(
                                `Use image option ${index + 1} (${option.imageUrl}) for ${candidate.attractionName}.`,
                                {
                                  type: 'select_image',
                                  attractionName: candidate.attractionName,
                                  imageUrl: option.imageUrl,
                                }
                              )
                            }
                          >
                            {candidate.selectedImageUrl === option.imageUrl
                              ? 'Selected'
                              : `Use ${index + 1}`}
                          </Button>
                          <a
                            href={option.sourceUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="small"
                          >
                            Source
                          </a>
                        </div>
                      ))}
                    </div>
                    <Button
                      size="sm"
                      variant="link"
                      className="px-0"
                      disabled={sending}
                      onClick={() =>
                        sendMessage(
                          `Skip artwork for ${candidate.attractionName}.`,
                          {
                            type: 'select_image',
                            attractionName: candidate.attractionName,
                            imageUrl: null,
                          }
                        )
                      }
                    >
                      Skip artwork
                    </Button>
                  </div>
                ))}
              </section>
            ) : null}

            {!result && artworkUnavailable ? (
              <section className="draft-events-assistant__card">
                <div className="fw-semibold mb-1">Artwork unavailable</div>
                <div className="small text-muted mb-2">
                  None of the researched images could be loaded. Continue
                  without artwork and add it manually, or ask me to look again.
                </div>
                <Button
                  size="sm"
                  variant="outline-primary"
                  disabled={sending}
                  onClick={() =>
                    sendMessage('Continue without any artwork.', {
                      type: 'confirm_images',
                    })
                  }
                >
                  Continue without artwork
                </Button>
              </section>
            ) : null}

            {!result && needsArtworkApproval ? (
              <section className="draft-events-assistant__card">
                <div className="fw-semibold mb-1">Artwork approval</div>
                <div className="small text-muted mb-2">
                  Approve the images you picked. Anything you did not pick uses
                  the first researched option, and skipped artwork stays empty.
                </div>
                <Button
                  size="sm"
                  variant="outline-primary"
                  disabled={sending}
                  onClick={() =>
                    sendMessage(
                      'I confirm all selected images and all choices marked to skip.',
                      { type: 'confirm_images' }
                    )
                  }
                >
                  Confirm artwork choices
                </Button>
              </section>
            ) : null}

            {citations.length > 0 ? (
              <details className="draft-events-assistant__sources">
                <summary>Research sources</summary>
                <ul className="mb-0 mt-2">
                  {citations.map((citation) => (
                    <li key={citation.url}>
                      <a href={citation.url} target="_blank" rel="noreferrer">
                        {citation.title || citation.url}
                      </a>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}

            {!result && state.packageId ? (
              <Form.Check
                type="switch"
                id="draft-events-assistant-season-package"
                label="Also create a season package"
                checked={state.createSeasonPackage}
                disabled={sending}
                onChange={(event) =>
                  sendMessage(
                    event.target.checked
                      ? 'Also create a season package.'
                      : 'Do not create a season package.',
                    {
                      type: 'set_create_season_package',
                      value: event.target.checked,
                    }
                  )
                }
              />
            ) : null}

            {!result && suggestionGroups.length > 0
              ? suggestionGroups.map((group) => (
                  <section
                    className="draft-events-assistant__card"
                    key={group.key}
                  >
                    <div className="fw-semibold mb-2">{group.title}</div>
                    <div className="d-flex flex-wrap gap-2">
                      {group.options.map((option, index) => (
                        <Button
                          key={optionKey(option, index)}
                          size="sm"
                          variant={
                            group.isSelected(option)
                              ? 'success'
                              : 'outline-primary'
                          }
                          disabled={sending}
                          onClick={() =>
                            sendMessage(
                              group.buildMessage(option, index),
                              group.buildAction(option, index)
                            )
                          }
                        >
                          {group.buildLabel
                            ? group.buildLabel(option, index)
                            : optionLabel(option, index)}
                        </Button>
                      ))}
                    </div>
                  </section>
                ))
              : null}

            {!result && state.mode && missing.length > 0 ? (
              <section className="draft-events-assistant__card">
                <div className="fw-semibold mb-1">
                  Still needed before I create
                </div>
                <ul className="small mb-0 ps-3">
                  {missing.map((item) => (
                    <li key={item.key}>{item.label}</li>
                  ))}
                </ul>
              </section>
            ) : null}

            {result ? (
              <section className="draft-events-assistant__notice draft-events-assistant__notice--success">
                <div className="draft-events-assistant__notice-title">
                  {result.mode === 'season_package'
                    ? result.packageUuid || result.seasonPackageId
                      ? 'Created unpublished draft package'
                      : 'Created season'
                    : `Created ${result.eventCount || 0} unpublished draft ${
                        result.eventCount === 1 ? 'event' : 'events'
                      }`}
                </div>
                <dl className="draft-events-assistant__summary">
                  {resultRows.map(([label, value]) => (
                    <Fragment key={label}>
                      <dt>{label}</dt>
                      <dd>{value}</dd>
                    </Fragment>
                  ))}
                </dl>
                <p className="draft-events-assistant__notice-footnote">
                  {result.mode === 'season_package'
                    ? 'Find the season under Seasons and the package under Season Packages. Packages stay unpublished until you finish setup and publish them.'
                    : 'Find them under Events. They stay unpublished until you publish them.'}
                </p>
              </section>
            ) : null}

            {sending ? (
              <section
                className="draft-events-assistant__progress"
                aria-live="polite"
              >
                <button
                  type="button"
                  className="draft-events-assistant__progress-head"
                  aria-expanded={traceOpen}
                  onClick={() => setTraceOpen((current) => !current)}
                >
                  <span
                    className="draft-events-assistant__progress-dot"
                    aria-hidden="true"
                  />
                  <span className="draft-events-assistant__progress-copy">
                    Stella is thinking…
                  </span>
                  <span
                    className={`draft-events-assistant__progress-chevron${
                      traceOpen ? ' is-open' : ''
                    }`}
                    aria-hidden="true"
                  >
                    <ChevronIcon width={14} height={14} />
                  </span>
                  <span className="draft-events-assistant__progress-timer">
                    {formatElapsed(elapsedSeconds)}
                  </span>
                </button>

                {traceOpen ? (
                  <div className="draft-events-assistant__trace">
                    <div className="draft-events-assistant__trace-step">
                      <span
                        className="draft-events-assistant__trace-icon"
                        aria-hidden="true"
                      >
                        <TraceIcon width={14} height={14} />
                      </span>
                      <div className="draft-events-assistant__trace-body">
                        <div className="draft-events-assistant__trace-title">
                          {loadingCopy()}
                        </div>

                        {showProgressBar ? (
                          <div className="draft-events-assistant__progress-meter">
                            <div
                              className="draft-events-assistant__progress-track"
                              role="progressbar"
                              aria-valuenow={progressCurrent}
                              aria-valuemin={0}
                              aria-valuemax={progressTotal}
                            >
                              <span style={{ width: `${progressPercent}%` }} />
                            </div>
                            <span className="draft-events-assistant__progress-count">
                              {progressCurrent} of {progressTotal}
                            </span>
                          </div>
                        ) : null}

                        {creationSteps.length > 0 ? (
                          <ul className="draft-events-assistant__progress-steps">
                            {creationSteps.map((step) => (
                              <li key={step.key}>{step.label}</li>
                            ))}
                          </ul>
                        ) : null}

                        {isCreating ? (
                          <p className="draft-events-assistant__progress-note">
                            Larger batches can take a minute. Keep this panel
                            open until it finishes.
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : null}
              </section>
            ) : null}

            {error ? (
              <section className="draft-events-assistant__notice draft-events-assistant__notice--danger">
                <div className="draft-events-assistant__notice-head">
                  <div className="draft-events-assistant__notice-title">
                    Nothing was created yet
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="link"
                    className="draft-events-assistant__notice-dismiss"
                    onClick={() => setError('')}
                  >
                    Dismiss
                  </Button>
                </div>
                <p className="draft-events-assistant__notice-body">{error}</p>
              </section>
            ) : null}
          </div>

          <Form
            className="draft-events-assistant__composer"
            onSubmit={(event) => {
              event.preventDefault()
              sendMessage()
            }}
          >
            <div className="draft-events-assistant__context">
              <span aria-hidden="true">
                <ContextIcon width={13} height={13} />
              </span>
              <span className="draft-events-assistant__context-label">
                Context:
              </span>
              <span className="draft-events-assistant__context-value">
                {organization.name || 'Active organization'}
              </span>
            </div>

            <div
              className={`draft-events-assistant__field${
                dictation.listening ? ' is-listening' : ''
              }`}
            >
              {attachment ? (
                <div className="draft-events-assistant__attachment">
                  <PaperclipIcon width={14} height={14} />
                  <span title={attachment.name}>{attachment.name}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${attachment.name}`}
                    disabled={sending}
                    onClick={() => {
                      setAttachment(null)
                      if (fileInputRef.current) {
                        fileInputRef.current.value = ''
                      }
                    }}
                  >
                    <CloseIcon width={13} height={13} />
                  </button>
                </div>
              ) : null}
              <Form.Control
                as="textarea"
                rows={2}
                value={prompt}
                disabled={sending}
                onChange={(event) => setPrompt(event.target.value)}
                // Enter sends and Shift+Enter breaks the line, which is what a
                // chat composer is expected to do even though this is a form.
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault()
                    sendMessage()
                  }
                }}
                placeholder="Describe what you want to know or do next"
                aria-label="Message Ask Stella"
              />

              <div className="draft-events-assistant__field-actions">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_ATTACHMENT_TYPES}
                  className="visually-hidden"
                  aria-label="Attach a CSV, Excel, JSON, or PNG file"
                  disabled={sending}
                  onChange={(event) => {
                    const file = event.target.files?.[0] || null
                    if (file && file.size > MAX_ATTACHMENT_BYTES) {
                      setAttachment(null)
                      event.target.value = ''
                      setError(
                        'The selected file is larger than the 10 MB limit.'
                      )
                      return
                    }
                    setAttachment(file)
                    setError('')
                  }}
                />
                <button
                  type="button"
                  className="draft-events-assistant__attach"
                  disabled={sending}
                  aria-label="Attach file"
                  title="Attach CSV, Excel, JSON, or PNG"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <PaperclipIcon />
                </button>
                {dictation.supported ? (
                  <button
                    type="button"
                    className={`draft-events-assistant__mic${
                      dictation.listening ? ' is-listening' : ''
                    }`}
                    disabled={sending}
                    aria-pressed={dictation.listening}
                    aria-label={
                      dictation.listening ? 'Stop dictation' : 'Speak'
                    }
                    title={dictation.listening ? 'Stop dictation' : 'Speak'}
                    onClick={() =>
                      dictation.listening ? dictation.stop() : dictation.start()
                    }
                  >
                    {dictation.listening ? <StopIcon /> : <MicIcon />}
                  </button>
                ) : null}
                <button
                  type="submit"
                  className="draft-events-assistant__send"
                  disabled={(!prompt.trim() && !attachment) || sending}
                  aria-label="Send"
                  title="Send"
                >
                  {sending ? (
                    <Spinner as="span" size="sm" animation="border" />
                  ) : (
                    <SendIcon />
                  )}
                </button>
              </div>
            </div>

            {dictation.listening ? (
              <div
                className="draft-events-assistant__dictation"
                aria-live="polite"
              >
                <span
                  className="draft-events-assistant__dictation-dot"
                  aria-hidden="true"
                />
                <span className="draft-events-assistant__dictation-copy">
                  {dictation.interimText ||
                    'Listening… speak your request, then review it before sending.'}
                </span>
              </div>
            ) : null}

            {playback.speaking ? (
              <div className="draft-events-assistant__speaking" aria-live="off">
                <span
                  className="draft-events-assistant__speaking-dot"
                  aria-hidden="true"
                />
                <span className="draft-events-assistant__speaking-copy">
                  {playback.preparing
                    ? 'Stella is getting ready to speak…'
                    : 'Stella is speaking…'}
                </span>
                <button
                  type="button"
                  className="draft-events-assistant__speaking-stop"
                  onClick={playback.stop}
                >
                  Stop
                </button>
              </div>
            ) : null}

            {dictation.error ? (
              <div className="draft-events-assistant__dictation-error">
                {dictation.error}
              </div>
            ) : null}

            {playback.error ? (
              <div className="draft-events-assistant__dictation-error">
                {playback.error}
              </div>
            ) : null}

            <p className="draft-events-assistant__disclaimer">
              <span aria-hidden="true">
                <InfoIcon width={12} height={12} />
              </span>
              Uses AI. Verify results.
            </p>
          </Form>
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  )
}
